import { useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { SalesRequest } from "~/types/client";
import { TransportData } from "~/types/dispatch";

interface UseDispatchGuideOptions {
  onSuccess?: () => Promise<void>;
  refreshPageWithParams?: boolean;
}

export const useDispatchGuide = (
  onSuccessOrOptions?: (() => Promise<void>) | UseDispatchGuideOptions
) => {
  const [isCreatingDispatchGuide, setIsCreatingDispatchGuide] = useState<
    string | null
  >(null);

  // Handle both old function signature and new options object
  const options =
    typeof onSuccessOrOptions === "function"
      ? { onSuccess: onSuccessOrOptions, refreshPageWithParams: false }
      : onSuccessOrOptions || { refreshPageWithParams: false };

  const createDispatchGuide = async (
    order: SalesRequest,
    transport: TransportData,
    observations?: string
  ) => {
    setIsCreatingDispatchGuide(order.id);

    try {
      // Call the create-guia-despacho Edge Function
      const { data, error } = await supabase.functions.invoke(
        "create-guia-despacho",
        {
          body: {
            sales_request_id: order.id,
            transport,
            observations: observations || "",
          },
        }
      );

      if (error) {
        throw new Error(error.message || "Failed to create dispatch guide");
      }

      if (!data.success) {
        throw new Error(data.error || "Failed to create dispatch guide");
      }

      toast({
        title: "Guía de despacho creada",
        description: `Se ha creado exitosamente la guía de despacho para el pedido.`,
        variant: "default",
      });

      // Handle success based on options
      if (options.refreshPageWithParams) {
        // Refresh page with query params to open the dialog
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("open", order.id);
        currentUrl.searchParams.set("guia_generated", "true");

        // If we're on a client detail page, add tab parameter to ensure sales-requests tab is active
        if (currentUrl.pathname.includes("/clients/")) {
          currentUrl.searchParams.set("tab", "sales-requests");
        }

        window.location.href = currentUrl.toString();
      } else if (options.onSuccess) {
        // Call the onSuccess callback
        await options.onSuccess();
      }
    } catch (error) {
      console.error("Error creating dispatch guide:", error);

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Error desconocido al crear la guía de despacho";

      toast({
        title: "Error al crear guía de despacho",
        description: errorMessage,
        variant: "destructive",
      });

      // Re-throw the error so parent components can handle it
      throw error;
    } finally {
      setIsCreatingDispatchGuide(null);
    }
  };

  return {
    isCreatingDispatchGuide,
    createDispatchGuide,
  };
};
